import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateLineFriend } from 'api/line';
import { queryKeys } from 'constants/queryKeys';
import { userStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { TUpdateLineFriendRequest } from 'types';
import { issueMessage } from 'utils/scenarioIssues';

type UseUpdateLineFriendOptions = {
  onSuccess?: () => void;
};

type UseUpdateLineFriendResult = {
  update: (userLineId: number, request: TUpdateLineFriendRequest) => void;
  isSubmitting: boolean;
  error: string | undefined;
};

export function useUpdateLineFriend(
  options: UseUpdateLineFriendOptions = {},
): UseUpdateLineFriendResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | undefined>();

  const mutation = useMutation({
    mutationFn: async (params: {
      userLineId: number;
      request: TUpdateLineFriendRequest;
    }) => {
      if (!user) {
        throw new Error(userStrings.edit.errors.unauthorized);
      }

      return updateLineFriend(params.userLineId, params.request);
    },
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.LINE_FRIENDS, user?.uid],
      });
      queryClient.invalidateQueries({
        queryKey: [
          queryKeys.LINE_FRIEND_DETAIL,
          variables.userLineId,
          user?.uid,
        ],
      });
      // チャット側にも編集後の名前を反映させる（チャットヘッダー詳細・チャンネル一覧）。
      // どちらも literal なクエリキー先頭一致で再取得をトリガーする。
      queryClient.invalidateQueries({ queryKey: ['channel-detail'] });
      queryClient.invalidateQueries({ queryKey: ['management-channels'] });
      // タグの付け外しでタグ管理画面の友だち数が変わるため再取得する
      queryClient.invalidateQueries({
        queryKey: [queryKeys.TAGS, user?.uid],
      });
      setError(undefined);
      options.onSuccess?.();
    },
    onError: (caught) => {
      // タグ付与はステップ配信を開始させるため、その失敗理由もそのまま利用者へ伝える
      setError(issueMessage(caught, userStrings.edit.errors.updateFailed));
    },
  });

  const update = (userLineId: number, request: TUpdateLineFriendRequest) => {
    setError(undefined);
    mutation.mutate({ userLineId, request });
  };

  return {
    update,
    isSubmitting: mutation.isPending,
    error,
  };
}
